import { MessageType, type LootRefusalReason, type PickUpRefusalReason } from "odyssey-shared";
import type { WireLine } from "./lines.ts";

/**
 * The loot frames the page reads (P2.8-T16, ADR-091 decision 20, PROTOCOL.md 6): the server's `loot` and
 * `lootTaken`, each as one event the chest store takes, and `lootGained` (P2.8-T62), what a creature's drop moved into
 * the bag and what it lost, which the loot toasts take, and `pickUpRefused` (P2.8-T64), a ground item's pick-up refused,
 * which the page's pick-up takes (P2.8-T65). The text schema has already checked every field when a
 * message reaches here (a line from 0 to 7, at most eight of them, a quantity from 1 to 999, an item id of the rules'
 * pattern, each closed set), so nothing is checked twice.
 */

/**
 * What the server says of a chest: its lines for this character, or that they cannot be read now (`loot`); or which
 * lines a take moved to the bag and which it refused, each with its reason (`lootTaken`).
 */
export type ChestEvent =
  | { readonly kind: "loot"; readonly handle: number; readonly result: "open" | "unavailable"; readonly lines: readonly WireLine[] }
  | { readonly kind: "taken"; readonly handle: number; readonly taken: readonly number[]; readonly refused: readonly (readonly [line: number, reason: LootRefusalReason])[] };

/**
 * What a creature's drop gave the character (`lootGained`): the lines moved into the bag and the lines lost, each an
 * item and a quantity, the lines put on the ground for it (P2.8-T63), each the ground item's handle, item and
 * quantity, the copper paid, 0 for none, and whether the cap refused the coins (P2.8-T39).
 */
export interface GainedEvent {
  readonly kind: "gained";
  readonly items: readonly (readonly [item: string, quantity: number])[];
  readonly lost: readonly (readonly [item: string, quantity: number])[];
  readonly grounded: readonly (readonly [handle: number, item: string, quantity: number])[];
  readonly coin: number;
  readonly coinRefused: boolean;
}

/**
 * A pick-up of a ground item the server refused (`pickUpRefused`): the handle as sent, the first check that failed, and
 * for `owned` the time until the item turns public (6.56.0), 0 otherwise.
 */
export interface PickUpRefusedEvent {
  readonly kind: "pickUpRefused";
  readonly handle: number;
  readonly reason: PickUpRefusalReason;
  readonly waitMs: number;
}

/**
 * A loot frame: a chest's, a creature drop's, or a ground item's refused pick-up.
 */
export type LootEvent = ChestEvent | GainedEvent | PickUpRefusedEvent;

/**
 * A loot frame as the chest store, the loot toasts or the pick-up take it, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The loot event, or null.
 * @example
 * readLoot({ type: MessageType.Loot, handle: 7, result: "open", lines: [[0, "minor_healing_potion", 2, false]] });
 * // { kind: "loot", handle: 7, result: "open", lines: [[0, "minor_healing_potion", 2, false]] }
 */
export function readLoot(message: Readonly<Record<string, unknown>> & { readonly type: number }): LootEvent | null {
  switch (message.type) {
    case MessageType.Loot:
      return { kind: "loot", handle: message["handle"] as number, result: message["result"] as "open" | "unavailable", lines: message["lines"] as readonly WireLine[] };
    case MessageType.LootGained:
      return { kind: "gained", items: message["items"] as GainedEvent["items"], lost: message["lost"] as GainedEvent["lost"], grounded: message["grounded"] as GainedEvent["grounded"], coin: message["coin"] as number, coinRefused: message["coinRefused"] as boolean };
    case MessageType.PickUpRefused:
      return { kind: "pickUpRefused", handle: message["handle"] as number, reason: message["reason"] as PickUpRefusalReason, waitMs: message["waitMs"] as number };
    case MessageType.LootTaken:
      return { kind: "taken", handle: message["handle"] as number, taken: message["taken"] as readonly number[], refused: message["refused"] as readonly (readonly [number, LootRefusalReason])[] };
    default:
      return null;
  }
}
